<script setup>
import { computed } from 'vue';

const props = defineProps({
    show: Boolean,
    type: {
        type: String,
        default: 'success', // 'success' | 'error'
    },
    title: String,
    message: String,
    details: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['close']);

const isSuccess = computed(() => props.type === 'success');

function close() {
    emit('close');
}
</script>

<template>
    <Transition name="modal">
        <div v-if="show" class="modal-overlay" @click.self="close">
            <div class="modal-container" :class="type">
                <div class="status-icon-wrapper">
                    <div class="status-icon">
                        <span class="material-symbols-rounded">{{ isSuccess ? 'check_circle' : 'error' }}</span>
                    </div>
                    <div class="status-ring"></div>
                </div>

                <h2 class="modal-title">{{ title }}</h2>
                <p class="modal-message">{{ message }}</p>

                <div v-if="isSuccess && (details.total || details.change)" class="details-card">
                    <div v-if="details.total" class="detail-row">
                        <span class="detail-label">Total pagado</span>
                        <span class="detail-value">${{ Number(details.total).toFixed(2) }}</span>
                    </div>
                    <div v-if="details.change > 0" class="detail-row highlight">
                        <span class="detail-label">Cambio</span>
                        <span class="detail-value">${{ Number(details.change).toFixed(2) }}</span>
                    </div>
                </div>

                <div v-if="isSuccess" class="ticket-info">
                    <span class="material-symbols-rounded">receipt_long</span>
                    <span>Ticket descargado automáticamente</span>
                </div>

                <button class="btn-action" @click="close">
                    {{ isSuccess ? 'Aceptar' : 'Intentar de nuevo' }}
                </button>
            </div>
        </div>
    </Transition>
</template>

<style scoped>
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 1rem;
}

.modal-container {
    background: white;
    border-radius: 24px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    width: 100%;
    max-width: 400px;
    padding: 2.5rem 2rem;
    text-align: center;
    position: relative;
    overflow: hidden;
    animation: popIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes popIn {
    from {
        opacity: 0;
        transform: scale(0.9) translateY(20px);
    }

    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

/* Icon Animation */
.status-icon-wrapper {
    position: relative;
    width: 80px;
    height: 80px;
    margin: 0 auto 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.status-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    position: relative;
}

.status-icon .material-symbols-rounded {
    font-size: 40px;
    color: white;
}

.status-ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    opacity: 0.2;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        transform: scale(1);
        opacity: 0.2;
    }

    50% {
        transform: scale(1.2);
        opacity: 0.1;
    }

    100% {
        transform: scale(1);
        opacity: 0.2;
    }
}

/* Success Styles */
.modal-container.success .status-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    box-shadow: 0 10px 20px rgba(16, 185, 129, 0.3);
}

.modal-container.success .status-ring {
    background: #10b981;
}

/* Error Styles */
.modal-container.error .status-icon {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    box-shadow: 0 10px 20px rgba(239, 68, 68, 0.3);
}

.modal-container.error .status-ring {
    background: #ef4444;
}

.modal-title {
    font-size: 1.5rem;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 0.5rem;
}

.modal-message {
    color: #6b7280;
    font-size: 1rem;
    margin: 0 0 1.5rem;
    line-height: 1.5;
}

.details-card {
    background: #f9fafb;
    border-radius: 16px;
    padding: 1rem;
    margin-bottom: 1.5rem;
    border: 1px solid #e5e7eb;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    font-size: 0.95rem;
}

.detail-row:not(:last-child) {
    border-bottom: 1px dashed #e5e7eb;
}

.detail-label {
    color: #6b7280;
    font-weight: 500;
}

.detail-value {
    color: #1f2937;
    font-weight: 700;
    font-size: 1.1rem;
}

.detail-row.highlight .detail-value {
    color: #10b981;
}

.ticket-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    color: #6366f1;
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 2rem;
    background: rgba(99, 102, 241, 0.1);
    padding: 0.5rem 1rem;
    border-radius: 99px;
    display: inline-flex;
}

.ticket-info .material-symbols-rounded {
    font-size: 18px;
}

.btn-action {
    width: 100%;
    padding: 1rem;
    border-radius: 12px;
    border: none;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.2s;
}

.modal-container.success .btn-action {
    background: #1f2937;
    color: white;
    box-shadow: 0 4px 12px rgba(31, 41, 55, 0.2);
}

.modal-container.success .btn-action:hover {
    background: #111827;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(31, 41, 55, 0.3);
}

.modal-container.error .btn-action {
    background: #f3f4f6;
    color: #374151;
}

.modal-container.error .btn-action:hover {
    background: #e5e7eb;
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
    .modal-overlay {
        background: rgba(0, 0, 0, 0.8);
    }

    .modal-container {
        background: #1e293b;
    }

    .modal-title {
        color: #e2e8f0;
    }

    .modal-message {
        color: #94a3b8;
    }

    .details-card {
        background: #0f172a;
        border-color: #334155;
    }

    .detail-row:not(:last-child) {
        border-color: #334155;
    }

    .detail-label {
        color: #94a3b8;
    }

    .detail-value {
        color: #e2e8f0;
    }

    .ticket-info {
        background: rgba(99, 102, 241, 0.2);
        color: #818cf8;
    }

    .modal-container.success .btn-action {
        background: #e2e8f0;
        color: #0f172a;
    }

    .modal-container.success .btn-action:hover {
        background: white;
    }

    .modal-container.error .btn-action {
        background: #334155;
        color: #e2e8f0;
    }

    .modal-container.error .btn-action:hover {
        background: #475569;
    }
}

:root[data-theme="dark"] {
    .modal-overlay {
        background: rgba(0, 0, 0, 0.8);
    }

    .modal-container {
        background: #1e293b;
    }

    .modal-title {
        color: #e2e8f0;
    }

    .modal-message {
        color: #94a3b8;
    }

    .details-card {
        background: #0f172a;
        border-color: #334155;
    }

    .detail-row:not(:last-child) {
        border-color: #334155;
    }

    .detail-label {
        color: #94a3b8;
    }

    .detail-value {
        color: #e2e8f0;
    }

    .ticket-info {
        background: rgba(99, 102, 241, 0.2);
        color: #818cf8;
    }

    .modal-container.success .btn-action {
        background: #e2e8f0;
        color: #0f172a;
    }

    .modal-container.success .btn-action:hover {
        background: white;
    }

    .modal-container.error .btn-action {
        background: #334155;
        color: #e2e8f0;
    }

    .modal-container.error .btn-action:hover {
        background: #475569;
    }
}
</style>
